import { useState, useEffect } from "react";
import { Shield, Edit, Trash2, RefreshCw, Plus, Users } from "lucide-react";
import { Checkbox } from "antd";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard } from "../../components/ui/cards";
import { DialogModal, ConfirmPopupModal } from "../../components/ui/dialogModal";
import { Input } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ROLES_URL, PERMISSION_CATALOG_URL } from "../../constants/api";

// One tick per page = full access. Backend still stores the four flags, so a
// granted page sets them all; a cleared page clears them all.
const ACTION_ORDER = ["view", "create", "edit", "delete"];
const ACTION_LABEL = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };
const NO_ACTIONS  = { view: false, create: false, edit: false, delete: false };

const RolesManager = () => {
    const { fetch, create, update } = useApiMethods();

    // Permission catalog (menu -> pages) fetched from the backend registry.
    const [catalog, setCatalog] = useState([]);   // [{menu, icon, pages:[{key,label,route,feature,links}]}]

    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, role: null });
    const [formData, setFormData] = useState({ id: null, name: "", description: "", max_discount_pct: 0, permissions: {} });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    // The backend catalog is already filtered to this tenant's plan (plan-gated
    // pages whose feature is disabled are omitted; core pages are always sent).
    const visibleMenus = catalog.filter(menu => (menu.pages || []).length > 0);

    // Flat key -> label map (for the roles table "Pages" column).
    const labelByKey = {};
    catalog.forEach(m => (m.pages || []).forEach(p => { labelByKey[p.key] = p.label; }));

    const blankPerms = () => {
        const perms = {};
        catalog.forEach(m => (m.pages || []).forEach(p => { perms[p.key] = { ...NO_ACTIONS }; }));
        return perms;
    };

    const loadCatalog = async () => {
        try {
            const res = await fetch(PERMISSION_CATALOG_URL);
            if (res?.status === "success") setCatalog(res.data || []);
        } catch {
            ToastMsg("error", "Failed to load permission catalog");
        }
    };

    const loadRoles = async () => {
        setLoading(true);
        try {
            const res = await fetch(ROLES_URL, {}, setLoading);
            if (res?.status === "success") setRoles(res.data || []);
        } catch {
            ToastMsg("error", "Failed to load roles");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadCatalog(); loadRoles(); }, []);

    const resetForm = () => {
        setFormData({ id: null, name: "", description: "", max_discount_pct: 0, permissions: blankPerms() });
        setEditMode(false);
        setOpenDialog(false);
    };

    const handleOpenCreate = () => {
        setFormData({ id: null, name: "", description: "", max_discount_pct: 0, permissions: blankPerms() });
        setEditMode(false);
        setOpenDialog(true);
    };

    const handleOpenEdit = (role) => {
        // Load each action as stored. This used to collapse to all-or-nothing,
        // which meant opening and saving a role silently granted create/edit/
        // delete on every page it could merely view.
        const perms = blankPerms();
        if (role.permissions) {
            Object.keys(perms).forEach(key => {
                const src = role.permissions[key];
                if (src) {
                    perms[key] = {
                        view: !!src.view, create: !!src.create,
                        edit: !!src.edit, delete: !!src.delete,
                    };
                }
            });
        }
        setFormData({
            id: role.id,
            name: role.name,
            description: role.description || "",
            max_discount_pct: role.max_discount_pct ?? 0,
            permissions: perms,
        });
        setEditMode(true);
        setOpenDialog(true);
    };

    // Which actions a page can be granted. The backend declares this per page
    // (reports are view-only), so the grid never shows a box that grants nothing.
    const actionsFor = (pg) => (pg.actions && pg.actions.length ? pg.actions : ACTION_ORDER);

    /**
     * Set one action on one page.
     *
     * View is a prerequisite, not an independent flag: create/edit/delete are
     * meaningless on a page the user cannot open, and every backend handler
     * reads before it writes. So granting any of them grants view, and removing
     * view removes them all — rather than leaving a role that may delete a
     * record it cannot see.
     */
    const setPageAction = (pg, action, granted) => {
        const allowed = actionsFor(pg);
        setFormData(prev => {
            const current = prev.permissions[pg.key] || { ...NO_ACTIONS };
            let next;
            if (action === 'view') {
                next = granted ? { ...current, view: true } : { ...NO_ACTIONS };
            } else {
                next = { ...current, [action]: granted };
                if (granted) next.view = true;
            }
            // Never store an action this page does not support.
            ACTION_ORDER.forEach(a => { if (!allowed.includes(a)) next[a] = false; });
            return { ...prev, permissions: { ...prev.permissions, [pg.key]: next } };
        });
    };

    // Toggle every page under a menu at once.
    const setMenuAccess = (menu, granted) => {
        setFormData(prev => {
            const permissions = { ...prev.permissions };
            menu.pages.forEach(pg => {
                const next = { ...NO_ACTIONS };
                if (granted) actionsFor(pg).forEach(a => { next[a] = true; });
                permissions[pg.key] = next;
            });
            return { ...prev, permissions };
        });
    };

    const isPageGranted = (key) => !!formData.permissions[key]?.view;
    const hasAction = (key, action) => !!formData.permissions[key]?.[action];

    const menuState = (menu) => {
        const total = menu.pages.length;
        const granted = menu.pages.filter(pg => isPageGranted(pg.key)).length;
        return { all: granted === total && total > 0, some: granted > 0 && granted < total, none: granted === 0 };
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            ToastMsg("error", "Role name is required");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                id: formData.id,
                name: formData.name.trim(),
                description: formData.description.trim(),
                max_discount_pct: Number(formData.max_discount_pct) || 0,
                permissions: formData.permissions,
            };
            const res = editMode
                ? await update(ROLES_URL, payload, false, setSaving)
                : await create(ROLES_URL, payload, false, setSaving);
            if (res?.status === "success") {
                ToastMsg("success", res.message || `Role ${editMode ? "updated" : "created"} successfully`);
                resetForm();
                loadRoles();
            } else {
                ToastMsg("error", res?.message || `Failed to ${editMode ? "update" : "create"} role`);
            }
        } catch {
            ToastMsg("error", "An error occurred");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        const role = deleteConfirm.role;
        if (!role) return;
        try {
            const res = await update(ROLES_URL, { id: role.id, is_deleted: true }, false);
            if (res?.status === "success") {
                ToastMsg("success", "Role deleted successfully");
                loadRoles();
            } else {
                ToastMsg("error", res?.message || "Failed to delete role");
            }
        } catch {
            ToastMsg("error", "An error occurred");
        } finally {
            setDeleteConfirm({ open: false, role: null });
        }
    };

    const filtered = roles.filter(r => {
        const matchSearch = !search || r.name.toLowerCase().includes(search.toLowerCase());
        const matchStatus = statusFilter === "all"
            ? true
            : statusFilter === "active" ? r.is_active : !r.is_active;
        return matchSearch && matchStatus;
    });

    const activeCount   = roles.filter(r => r.is_active).length;
    const inactiveCount = roles.filter(r => !r.is_active).length;

    const grantedLabels = (perms) => {
        if (!perms) return "—";
        const labels = Object.entries(perms)
            .filter(([, p]) => p?.view)
            .filter(([key]) => labelByKey[key])   // only pages still in the catalog
            .map(([key]) => labelByKey[key]);
        return labels.join(", ") || "None";
    };

    return (
        <ContainerCard>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white">Roles</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        Create custom roles and tick the pages each role can access
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => { loadCatalog(); loadRoles(); }}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </button>
                    <button
                        onClick={handleOpenCreate}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Add Role
                    </button>
                </div>
            </div>

            {/* Filter badges + search */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {[
                    { key: "all",      label: `All · ${roles.length}` },
                    { key: "active",   label: `Active · ${activeCount}` },
                    { key: "inactive", label: `Inactive · ${inactiveCount}` },
                ].map(f => (
                    <button
                        key={f.key}
                        onClick={() => setStatusFilter(f.key)}
                        className={cn(
                            "px-3 py-1 text-xs font-medium rounded-full border transition-colors",
                            statusFilter === f.key
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-blue-400"
                        )}
                    >
                        {f.label}
                    </button>
                ))}
                <input
                    type="text"
                    placeholder="Search roles..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="ml-auto px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 w-48"
                />
            </div>

            {/* Table */}
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            {["#", "Role Name", "Description", "Users", "Pages", "Status", "Actions"].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {loading ? (
                            <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td>
                            </tr>
                        ) : filtered.length === 0 ? (
                            <tr>
                                <td colSpan={7} className="px-4 py-8 text-center text-gray-400">No roles found</td>
                            </tr>
                        ) : filtered.map((role, idx) => (
                            <tr key={role.id} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{idx + 1}</td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <Shield className="h-4 w-4 text-blue-500 shrink-0" />
                                        <span className="font-medium text-gray-900 dark:text-white">{role.name}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-[200px] truncate">
                                    {role.description || "—"}
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1 text-gray-600 dark:text-gray-400">
                                        <Users className="h-3.5 w-3.5" />
                                        <span>{role.user_count ?? 0}</span>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-gray-500 dark:text-gray-400 max-w-[180px] truncate text-xs">
                                    {grantedLabels(role.permissions)}
                                </td>
                                <td className="px-4 py-3">
                                    <span className={cn(
                                        "px-2 py-0.5 text-xs font-medium rounded-full",
                                        role.is_active
                                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                                    )}>
                                        {role.is_active ? "Active" : "Inactive"}
                                    </span>
                                </td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => handleOpenEdit(role)}
                                            className="p-1.5 rounded text-blue-600 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                                            title="Edit"
                                        >
                                            <Edit className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm({ open: true, role })}
                                            className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Dialog */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onSave={handleSave}
                title={editMode ? "Edit Role" : "Add New Role"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Create"}
                editMode={editMode}
                loading={saving}
                width="640px"
            >
                <div className="space-y-4">
                    {/* Name & Description */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Role Name <span className="text-red-500">*</span>
                            </label>
                            <Input
                                value={formData.name}
                                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                                placeholder="e.g. Sales Manager"
                                disabled={saving}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Description
                            </label>
                            <Input
                                value={formData.description}
                                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                                placeholder="Optional description"
                                disabled={saving}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Max discount % (without approval)
                            </label>
                            <Input
                                type="number"
                                step="0.01"
                                value={formData.max_discount_pct}
                                onChange={e => setFormData(p => ({ ...p, max_discount_pct: e.target.value }))}
                                placeholder="0"
                                disabled={saving}
                            />
                            <p className="text-[10px] text-gray-500">
                                Quotes with discount above this % need approval from a higher-cap role.
                            </p>
                        </div>
                    </div>

                    {/* Page access — one tick per page grants full access (view + all actions) */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                Page Access
                            </p>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500">
                                Tick a page to allow viewing and all actions. Tick a menu to select every page under it.
                            </p>
                        </div>
                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-200 dark:divide-gray-700 max-h-[420px] overflow-y-auto">
                            {visibleMenus.length === 0 ? (
                                <div className="px-4 py-6 text-center text-xs text-gray-400">
                                    No pages available for this plan.
                                </div>
                            ) : visibleMenus.map(menu => {
                                const st = menuState(menu);
                                const singlePage = menu.pages.length === 1 && menu.pages[0].label === menu.menu;
                                return (
                                    <div key={menu.menu} className="bg-white dark:bg-gray-800">
                                        {/* Menu header with select-all checkbox */}
                                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-700/50">
                                            <Checkbox
                                                checked={st.all}
                                                indeterminate={st.some}
                                                disabled={saving}
                                                onChange={e => setMenuAccess(menu, e.target.checked)}
                                            />
                                            <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider flex-1 min-w-0 truncate">
                                                {menu.menu}
                                            </span>
                                            {!singlePage && (
                                                <div className="flex items-center gap-0 shrink-0">
                                                    {ACTION_ORDER.map(action => (
                                                        <span
                                                            key={action}
                                                            className="w-[68px] text-center text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
                                                        >
                                                            {ACTION_LABEL[action]}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {/* Pages — hidden when the menu is a single page named like the menu */}
                                        {!singlePage && menu.pages.map(pg => {
                                            const allowed = actionsFor(pg);
                                            return (
                                                <div
                                                    key={pg.key}
                                                    className="flex items-center gap-3 px-4 py-2 pl-10 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                                                >
                                                    <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 min-w-0 truncate">
                                                        {pg.label}
                                                    </span>
                                                    {/* One column per action, aligned across every row. A page that
                                                        cannot be edited or deleted leaves those columns blank rather
                                                        than offering a switch that grants nothing. */}
                                                    <div className="flex items-center gap-0 shrink-0">
                                                        {ACTION_ORDER.map(action => (
                                                            <div key={action} className="w-[68px] flex justify-center">
                                                                {allowed.includes(action) ? (
                                                                    <label
                                                                        className="flex items-center justify-center cursor-pointer p-1"
                                                                        title={`${ACTION_LABEL[action]} — ${pg.label}`}
                                                                    >
                                                                        <Checkbox
                                                                            checked={hasAction(pg.key, action)}
                                                                            disabled={saving}
                                                                            onChange={e => setPageAction(pg, action, e.target.checked)}
                                                                        />
                                                                    </label>
                                                                ) : (
                                                                    <span className="text-gray-300 dark:text-gray-600 select-none">–</span>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </DialogModal>

            {/* Delete Confirmation */}
            <ConfirmPopupModal
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, role: null })}
                onConfirm={handleDelete}
                title="Delete Role"
                description={`Are you sure you want to delete the role "${deleteConfirm.role?.name}"? Users assigned to this role will lose their permissions.`}
                type="danger"
                confirmLabel="Delete"
                destructive={true}
            />
        </ContainerCard>
    );
};

export default RolesManager;
